import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useCustomerService } from 'services/customers';
import { Customer } from 'types/customer';
import Button from 'components/Button';
import styles from './Customers.module.scss';

interface EditCustomerModalProps {
  show: boolean;
  onHide: () => void;
  customer: Customer | null;
  onSuccess: (customer: Customer) => void;
  onError: (message: string) => void;
}

function EditCustomerModal({
  show,
  onHide,
  customer,
  onSuccess,
  onError,
}: EditCustomerModalProps) {
  const customerService = useCustomerService();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [tinNumber, setTinNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (customer) {
      setName(customer.name || '');
      setEmail(customer.email || '');
      setPhone(customer.phone || '');
      setAddress(customer.address || '');
      setTinNumber(customer.tinNumber || '');
      setNotes(customer.notes || '');
    }
  }, [customer, show]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!customer) return;

    if (!name.trim()) {
      onError('Customer name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedCustomer = await customerService.updateCustomer(
        customer.id,
        {
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          tinNumber: tinNumber.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      );
      onSuccess(updatedCustomer);
      onHide();
    } catch (error) {
      onError(
        error instanceof Error ? error.message : 'Failed to update customer',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!customer) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Edit Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(e);
          }}
        >
          <Form.Group controlId="customerName" className={styles.formGroup}>
            <Form.Label className={styles.label}>Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Customer name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="customerEmail" className={styles.formGroup}>
            <Form.Label className={styles.label}>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="customer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="customerPhone" className={styles.formGroup}>
            <Form.Label className={styles.label}>Phone</Form.Label>
            <Form.Control
              type="text"
              placeholder="+1234567890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="customerAddress" className={styles.formGroup}>
            <Form.Label className={styles.label}>Address</Form.Label>
            <Form.Control
              type="text"
              placeholder="123 Main St, City, State 12345"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group
            controlId="customerTinNumber"
            className={styles.formGroup}
          >
            <Form.Label className={styles.label}>TIN Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="123-456-789"
              value={tinNumber}
              onChange={(e) => setTinNumber(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="customerNotes" className={styles.formGroup}>
            <Form.Label className={styles.label}>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Additional notes about the customer..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={styles.textarea}
            />
          </Form.Group>

          <div className={styles.modalFooter}>
            <Button
              type="button"
              variant="outline"
              onClick={onHide}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Customer'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default EditCustomerModal;
